// Copyright (c) StrangeBee 2024

import { SearchScopeType } from '@Types';
import { FilterInput, ObjectTypeV1 } from 'thehive-sdk';
import useLocalStorage, { defaultFilters, defaultKeywords, LocalStorageKeys } from './useLocalStorage';

interface StorageSearch {
    activeScope?: SearchScopeType;
    keywords: Record<ObjectTypeV1, string>;
    filters: Record<ObjectTypeV1, FilterInput[]>;
}
interface UseStorageSearchReturn {
    activeScope?: SearchScopeType;
    filters: Record<ObjectTypeV1, FilterInput[]>;
    setSearchFilters: (fieldScope: SearchScopeType, filters: FilterInput[]) => void;
    setSearchActiveScope: (fieldScope: SearchScopeType) => void;
    keywords: Record<ObjectTypeV1, string>;
    setSearchKeyword: (fieldScope: SearchScopeType, keyword: string) => void;
}

const useStorageSearch = (): UseStorageSearchReturn => {
    const { storage, setStorage } = useLocalStorage<StorageSearch>({
        key: LocalStorageKeys.SEARCH,
        initialValue: (value) => ({
            ...value,
            filters: { ...defaultFilters, ...value?.filters },
            keywords: { ...defaultKeywords, ...value?.keywords },
        }),
    });

    const { activeScope, filters: storageFilters, keywords } = storage;

    const setSearchActiveScope = (fieldScope: SearchScopeType): void => {
        setStorage((storage) => ({ ...storage, activeScope: fieldScope }));
    };

    const setSearchFilters = (fieldScope: SearchScopeType, filters: FilterInput[]): void => {
        setStorage((storage) => ({
            ...storage,
            filters: { ...storageFilters, [fieldScope]: filters },
        }));
    };

    const setSearchKeyword = (fieldScope: SearchScopeType, keyword: string): void => {
        setStorage((storage) => ({
            ...storage,
            keywords: {
                ...storage.keywords,
                [fieldScope]: keyword,
            },
        }));
    };

    return { activeScope, filters: storageFilters, setSearchActiveScope, setSearchFilters, keywords, setSearchKeyword };
};

export default useStorageSearch;
